/**
 * Heuristic prompt-class detection for brand-focused and brand-comparison
 * prompts. Pure text matching — no DB, no LLM — so it runs identically
 * server-side (create/update suggestion endpoint) and in tests.
 *
 * Taxonomy: `solution_comparison` is category-based and names no brands;
 * `brand_comparison` names our brand against specific competitors. Explicit
 * brand naming is the discriminator, so every detection requires our brand
 * (or an alias) to appear in the prompt text.
 */

export interface PromptClassDetectionInput {
	query: string;
	brandName: string;
	aliases?: string[];
	competitors?: Array<{ name: string; aliases?: string[] }>;
}

export interface PromptClassSuggestion {
	suggestedType: "brand_identity" | "brand_comparison";
	/** Canonical competitor names matched in the prompt text (comparison only). */
	comparisonBrands: string[];
	/** "unambiguous" suggestions are safe to auto-accept; "suggestion" needs user confirmation. */
	confidence: "unambiguous" | "suggestion";
}

function escapeRegExp(value: string): string {
	return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * A case-insensitive, Unicode-aware whole-word pattern for `term`, or null when
 * the term is too short to be evidence of anything.
 *
 * Exported so a caller that tests MANY terms against MANY strings can compile
 * once instead of per comparison (see `competitor-named-query.ts`). The 2-char
 * floor is this module's, not a shared policy: `detectPromptClass` runs against
 * a prompt a human wrote, where a 2-character alias is a legitimate brand
 * reference. Callers scanning machine-generated text apply their own, higher
 * floor before calling.
 *
 * The lookarounds are `\p{L}\p{N}` rather than `\b` on purpose: `\b` is
 * ASCII-word-based, so it splits inside accented names and treats a trailing
 * "." or "-" as a boundary in ways that break domain-shaped terms.
 */
export function wordBoundaryPattern(term: string): RegExp | null {
	const trimmed = term.trim();
	if (trimmed.length < 2) {
		return null;
	}
	return new RegExp(
		`(?<![\\p{L}\\p{N}])${escapeRegExp(trimmed.normalize("NFKC"))}(?![\\p{L}\\p{N}])`,
		"iu",
	);
}

export function wordBoundaryMatch(text: string, term: string): boolean {
	const pattern = wordBoundaryPattern(term);
	return pattern ? pattern.test(text.normalize("NFKC")) : false;
}

const COMPARISON_PHRASES =
	/\bvs\.?\b|\bversus\b|\bcompared?\s+(?:to|with|against)\b|\bcomparison\s+(?:of|between)\b|\bbetter\s+than\b|\bor\s+should\s+i\b/i;

const IDENTITY_PHRASES = [
	/\b(?:what|who)\s+(?:is|are)\b/i,
	/\bwhat\s+does\b.*\b(?:do|offer|sell|provide|make|specialize)\b/i,
	/\btell\s+me\s+about\b/i,
	/\bis\b.*\b(?:legit|legitimate|reputable|trustworthy|reliable|safe)\b/i,
	/\b(?:reviews?|pricing|pros\s+and\s+cons)\s+(?:of|for)\b/i,
];

export function detectPromptClass(
	input: PromptClassDetectionInput,
): PromptClassSuggestion | null {
	const { query } = input;
	const ourTerms = [input.brandName, ...(input.aliases ?? [])].filter(
		Boolean,
	);
	const ourBrandMentioned = ourTerms.some((term) =>
		wordBoundaryMatch(query, term),
	);
	if (!ourBrandMentioned) {
		return null;
	}

	const matchedCompetitors: string[] = [];
	for (const competitor of input.competitors ?? []) {
		const terms = [competitor.name, ...(competitor.aliases ?? [])].filter(
			Boolean,
		);
		if (terms.some((term) => wordBoundaryMatch(query, term))) {
			matchedCompetitors.push(competitor.name);
		}
	}

	const hasComparisonPhrase = COMPARISON_PHRASES.test(query);
	if (matchedCompetitors.length > 0 || hasComparisonPhrase) {
		return {
			suggestedType: "brand_comparison",
			comparisonBrands: matchedCompetitors,
			confidence:
				matchedCompetitors.length > 0 && hasComparisonPhrase
					? "unambiguous"
					: "suggestion",
		};
	}

	const hasIdentityPhrase = IDENTITY_PHRASES.some((re) => re.test(query));
	return {
		suggestedType: "brand_identity",
		comparisonBrands: [],
		// Naming the brand at all disqualifies a prompt from discovery metrics,
		// but only identity phrasing makes the intent unambiguous.
		confidence: hasIdentityPhrase ? "unambiguous" : "suggestion",
	};
}
